import { HttpErrorResponse, HttpInterceptorFn } from '@angular/common/http';
import { catchError, from, switchMap, throwError } from 'rxjs';
import { readProblemBody } from '../models/problem-detail.model';

/**
 * Makes every failed API response readable the same way. A request made with
 * `responseType: 'blob'` — the exports, the one place the tier-gated features live — gets its
 * ProblemDetail back as an undecoded Blob, so the `code` inside it is invisible to everything
 * downstream. Decoding it here means the license and auth interceptors, the dialogs and
 * `describeApiError` all read `error.error.code` and never have to know how the request was made.
 *
 * It is listed last in `withInterceptors`, which makes it the innermost: an error response passes
 * through it before any other interceptor's `catchError`. Listed first it would decode the Blob
 * only after the license interceptor had already looked at it and found no code.
 *
 * Only the Blob path is asynchronous; a JSON failure passes through untouched and synchronously.
 */
export const problemDetailInterceptor: HttpInterceptorFn = (req, next) =>
  next(req).pipe(
    catchError((error: unknown) => {
      if (!(error instanceof HttpErrorResponse) || !(error.error instanceof Blob)) {
        return throwError(() => error);
      }
      return from(decodeBlobBody(error)).pipe(switchMap((decoded) => throwError(() => decoded)));
    }),
  );

async function decodeBlobBody(error: HttpErrorResponse): Promise<HttpErrorResponse> {
  return new HttpErrorResponse({
    error: await readProblemBody(error),
    headers: error.headers,
    status: error.status,
    statusText: error.statusText,
    url: error.url ?? undefined,
  });
}
